import { COPY, fill } from "../copy.js";
import { ACCENT } from "../accent.js";
import { Creature } from "../creature.js";
import { fa } from "../format.js";
import { Passage } from "../rich-text.js";
import { shelfEntries } from "./shelf.js";
import type { Route } from "../route.js";

/**
 * About.
 *
 * Longer and calmer than the landing page, and the place the reasoning goes. Somebody who clicks
 * through has decided the pitch was interesting and is now checking whether there is anything behind
 * it — so this is where the constraints get explained rather than softened.
 *
 * **Rebuilt because it was four paragraphs and two boxes**, which is a page that has been started
 * rather than written: a teacher deciding whether to set up a class got the pitch again in slower
 * words and no answer to any of the questions they would actually ask. It is now ordered the way
 * somebody reads it — what this is, what the games are, why an error does not end one, how "learnt"
 * is decided, what is compared, who controls what, what is stored, what it refuses to claim, and how
 * to begin.
 *
 * The refusals are not small print at the bottom. They are the argument, so they get a section of
 * their own with the same weight as everything else.
 *
 * Mobile-first: one column, a reading measure capped well under the viewport, and the action
 * full-width until there is room for it not to be.
 */
export function AboutPage({
  navigate,
  home,
}: {
  navigate: (route: Route) => void;
  /** The signed-in visitor's own surface, or null. See `SiteHeader`. */
  home: Route | null;
}) {
  // Computed rather than typed into the copy, for the same reason `landing.tsx` computes it: a
  // number written into the sentence outlives the bundle it described (it did, at sixteen).
  const gameCount = fa(shelfEntries().length);
  const gamesBody = [
    fill(COPY.site.aboutGamesBody[0], { count: gameCount }),
    ...COPY.site.aboutGamesBody.slice(1),
  ];

  return (
    <main className="mx-auto max-w-[660px] px-lg py-xl md:px-xl">
      <section className="pb-xl">
        <div className="mb-xl flex justify-center">
          <span
            className={`grid size-[104px] place-items-center rounded-full bg-card ${ACCENT.sunny.shadow}`}
          >
            <Creature kind="elephant" accent="sunny" size={72} />
          </span>
        </div>
        <h1 className="mb-xl text-center text-[clamp(27px,7vw,40px)] leading-[1.25] font-extrabold">
          {COPY.site.aboutTitle}
        </h1>
        <Passage text={COPY.site.aboutBody} gap="lg" />
      </section>

      {/*
        * The sections, each answering one question.
        *
        * Numbered by their accent rather than by a digit: six headings in a column all in the same ink
        * read as one long page, and the tinted rule beside each is what makes it scannable without
        * adding chrome.
        */}
      <div className="grid gap-lg pb-xl">
        <Section accent="mint" title={COPY.site.aboutGamesTitle} body={gamesBody} />
        <Section
          accent="watermelon"
          title={COPY.site.aboutTeachingTitle}
          body={COPY.site.aboutTeachingBody}
        />
        <Section
          accent="grape"
          title={COPY.site.aboutMasteryTitle}
          body={COPY.site.aboutMasteryBody}
        />
        <Section
          accent="sky"
          title={COPY.site.aboutCompareTitle}
          body={COPY.site.aboutCompareBody}
        />
        <Section
          accent="sunny"
          title={COPY.site.aboutControlTitle}
          body={COPY.site.aboutControlBody}
        />
      </div>

      {/*
        * Who the content was designed under, given its own card.
        *
        * Named rather than "designed by experts": an unnamed claim to expertise is one nobody can
        * check, and every other section on this page is written to be checkable.
        */}
      <section className="mb-xl rounded-xl bg-card px-lg py-lg shadow-card">
        <h2 className="mb-sm text-title">{COPY.site.aboutSupervisionTitle}</h2>
        <Passage text={COPY.site.aboutSupervisionBody} />
      </section>

      {/*
        * What it refuses to claim, set apart.
        *
        * On its own card rather than in the run of sections: it is the part somebody quotes back, and
        * the part that has to survive being read on its own.
        */}
      <section className="mb-xl rounded-xl bg-ink/4 px-lg py-lg">
        <h2 className="mb-md text-title">{COPY.site.aboutLimitsTitle}</h2>
        <Passage text={COPY.site.aboutLimitsBody} gap="md" />
      </section>

      <section className="grid gap-md pb-xl sm:grid-cols-2">
        <Panel title={COPY.site.aboutAgeTitle} body={COPY.site.aboutAgeBody} />
        <Panel title={COPY.site.aboutPrivacyTitle} body={COPY.site.aboutPrivacyBody} />
      </section>

      <section className="rounded-xl bg-card px-lg py-xl shadow-card">
        <h2 className="mb-md text-title">{COPY.site.aboutStartTitle}</h2>
        <Passage text={COPY.site.aboutStartBody} gap="md" />
        <button
          onClick={() => navigate(home ?? { kind: "enter" })}
          className={`mt-lg min-h-touch w-full rounded-pill px-xxl py-md text-heading text-card ${ACCENT.grape.bg} ${ACCENT.grape.shadow}`}
        >
          {COPY.site.heroCta}
        </button>
      </section>
    </main>
  );
}

/**
 * One section: a tinted rule, a heading, and its paragraphs.
 *
 * The accent classes are literal per branch rather than assembled, for the reason in `accent.ts`:
 * Tailwind finds classes by scanning source text, so a class built at runtime never reaches the
 * stylesheet and the element renders unstyled with no error anywhere.
 */
function Section({
  accent,
  title,
  body,
}: {
  accent: "mint" | "watermelon" | "grape" | "sky" | "sunny";
  title: string;
  body: readonly string[];
}) {
  const rule =
    accent === "mint"
      ? "bg-mint"
      : accent === "watermelon"
        ? "bg-watermelon"
        : accent === "grape"
          ? "bg-grape"
          : accent === "sky"
            ? "bg-sky"
            : "bg-sunny";

  return (
    <section className="flex gap-md">
      <span className={`mt-2 w-1 shrink-0 rounded-pill ${rule}`} aria-hidden />
      <div className="min-w-0">
        <h2 className="mb-sm text-title">{title}</h2>
        <Passage text={body} gap="md" />
      </div>
    </section>
  );
}

function Panel({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-lg bg-ink/4 p-lg">
      <h2 className="mb-xs text-heading">{title}</h2>
      <p className="text-small text-muted">{body}</p>
    </div>
  );
}
